import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { qformat, DATE_FORMATS } from "views_shared/app/javascript/utils/dateFormatters";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import OutletConfigModal from "../shared/OutletConfigModal";
import MediaProfileConfigModal from "../shared/MediaProfileConfigModal";

function ApprovalPanel({
  request,
  outletConfig,
  profileConfig,
  onActionComplete,
  onOutletConfigChange,
  onProfileConfigChange,
  reprocessing,
  onResubmit,
}) {
  const [freeToPitch, setFreeToPitch] = useState(request.free_to_pitch || outletConfig?.set_free || false);
  const [approvalTier, setApprovalTier] = useState("approve_and_notify");
  const [actionLoading, setActionLoading] = useState(null);
  const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);
  const [showGhostConfirm, setShowGhostConfirm] = useState(false);
  const [showOutletModal, setShowOutletModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setFreeToPitch(request.free_to_pitch || outletConfig?.set_free || false);
  }, [request.free_to_pitch, outletConfig?.set_free]);

  const isProduction = globalThis.Qwoted?.BUSINESS_ENVIRONMENT === "production";

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const performAction = async (action, body = {}) => {
    setActionLoading(action);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/admin/api/requests/${request.id}/${action}`, {
        method: "POST",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        onActionComplete();
        return true;
      }

      const data = await res.json();
      if (res.status === 409) {
        showToast(data.error || "Request was already actioned by another admin", "info");
        onActionComplete();
      } else if (res.status === 422) {
        showToast(data.error || "Action failed", "warning");
      } else {
        showToast("Action failed \u2014 please try again", "danger");
      }
      return false;
    } catch (err) {
      showToast("Action failed \u2014 please try again", "danger");
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = () => {
    if (approvalTier === "approve_and_notify") {
      setShowNotifyConfirm(true);
      return;
    }

    const params = { approval: { free_to_pitch: freeToPitch } };
    if (approvalTier === "approve_and_distribute") {
      params.approval.distribute = true;
    }
    performAction("approve", params);
  };

  const handleConfirmApproveNotify = async () => {
    setShowNotifyConfirm(false);
    await performAction("approve", { approval: { notify: true, free_to_pitch: freeToPitch } });
  };

  const handleGhost = () => performAction("ghost");
  const handleUnghost = () => performAction("unghost");
  const handleBumpInFeed = () => performAction("bump_to_top");

  const handleResubmitClick = async () => {
    if (!onResubmit) return;
    setActionLoading("resubmit");
    try {
      const result = await onResubmit();
      if (!result.ok) {
        showToast(result.error || "Re-evaluation failed", "warning");
      }
    } catch {
      showToast("Re-evaluation failed — please try again", "danger");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDistributeNow = async () => {
    await performAction("approve", { approval: { distribute: true } });
  };

  const handleNotifyNow = async () => {
    setShowNotifyConfirm(false);
    await performAction("approve", { approval: { notify: true, free_to_pitch: freeToPitch } });
  };

  const handleGhostApproved = () => {
    setShowGhostConfirm(true);
  };

  const handleConfirmGhostApproved = () => {
    setShowGhostConfirm(false);
    performAction("ghost");
  };

  const handleToggleFreeToPitch = async () => {
    const newValue = !request.free_to_pitch;
    setActionLoading("toggle_ftp");
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/interact/api/requests/${request.id}`, {
        method: "PATCH",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ data: { attributes: { free_to_pitch: newValue } } }),
      });
      if (!res.ok) {
        showToast("Failed to update free to pitch \u2014 please try again", "danger");
        return;
      }
      try {
        await fetch("/admin/api/comments", {
          method: "POST",
          headers: {
            ...authenticatedHeadersHash(),
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            comment: {
              commentable_type: "Interact::Request",
              commentable_id: request.id,
              body: newValue ? "Set free to pitch" : "Removed free to pitch",
            },
          }),
        });
      } catch {
        // Audit comment failed but the toggle itself succeeded — still refresh
      }
      onActionComplete();
    } catch {
      showToast("Failed to update free to pitch \u2014 please try again", "danger");
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePaid = async () => {
    const newValue = !request.paid;
    setActionLoading("toggle_paid");
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/admin/api/requests/${request.id}/edit`, {
        method: "PATCH",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ request: { paid: newValue } }),
      });
      if (!res.ok) {
        showToast("Failed to update fee-based setting \u2014 please try again", "danger");
        return;
      }
      onActionComplete();
    } catch {
      showToast("Failed to update fee-based setting \u2014 please try again", "danger");
    } finally {
      setActionLoading(null);
    }
  };

  const isPending = request.needs_manual_approval_at && !request.approved_at && !request.ghosted_at;
  const isApproved = request.approved_at && !request.ghosted_at;
  const isGhosted = !!request.ghosted_at;
  const isDistributed = !!request.distribution_approved_for_feed_at;
  const isNotified = !!request.distribution_approved_for_notifications_at;

  const approveOnlyTooltip = `Approves the request. It will be visible via API and search, but not in the promoter feed.${isProduction ? " Currently disabled \u2014 awaiting promoter feed launch." : ""}`;
  const distributeTooltip = "Approves and distributes to the promoter feed where experts can discover it.";
  const notifyTooltip =
    "Approves, distributes to feed, AND sends push/email notifications to matching experts. This is irreversible.";
  const ftpTooltip = "When checked, experts can pitch to this request without spending a credit.";
  const bumpInFeedTooltip =
    "Refreshes the request's posted timestamp so it lifts back up in the personalized feed. Use sparingly \u2014 cooldown of 1 hour applies.";
  const bumpInFeedDisabledTooltip = "Mark the post-edit review as reviewed first.";
  const bumpInFeedDisabled = !!request.needs_post_edit_review_at;

  return (
    <div className="card">
      <div className="card-body">
        {/* Toast */}
        {toast && (
          <div className={`alert alert-${toast.type} alert-dismissible fade show mb-3`} role="alert">
            {toast.message}
            <button type="button" className="btn-close" onClick={() => setToast(null)} />
          </div>
        )}

        {/* Post-edit review banner — shown when reporter edited distributed content and moderation flagged it */}
        {request.needs_post_edit_review_at && isApproved && (isDistributed || isNotified) && (
          <div className="alert alert-warning mb-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <i className="fa fa-exclamation-triangle me-2" />
                <strong>Content edited after distribution.</strong> The reporter edited this request after notifications
                were sent. Content moderation flagged the changes for review.
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ms-3 flex-shrink-0"
                onClick={() => performAction("dismiss_edit_review")}
                disabled={!!actionLoading}
              >
                {actionLoading === "dismiss_edit_review" ? "Dismissing..." : "Mark as Reviewed"}
              </button>
            </div>
          </div>
        )}

        {/* Reprocessing: spinner display while pipeline re-evaluates */}
        {reprocessing && (
          <div>
            <div className="d-flex align-items-center gap-2 mb-3">
              <i className="fa fa-spinner fa-spin text-info" />
              <h6 className="mb-0">Re-evaluating...</h6>
            </div>
            <p className="text-muted small">
              The approval pipeline is running content moderation checks. This page will update automatically when
              complete.
            </p>
            <p className="text-muted small mb-0">
              Outcome: auto-approved if all checks pass, or back to manual review with fresh results.
            </p>
          </div>
        )}

        {/* Pending: redesigned approval panel */}
        {isPending && !reprocessing && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Manual Review</h6>
              {request.automation_reason && (
                <span className="text-muted small">Reason: {request.automation_reason}</span>
              )}
            </div>

            {/* Approval box */}
            <div className="p-3 mb-3 border-start border-4 border-success">
              <h6 className="small fw-bold mb-2">Approval Options and Visibility</h6>

              <div className="mb-3">
                <div className="form-check mb-1">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="approvalTier"
                    id="tierApproveOnly"
                    value="approve_only"
                    checked={approvalTier === "approve_only"}
                    onChange={() => setApprovalTier("approve_only")}
                    disabled={isProduction || !!actionLoading}
                  />
                  <label
                    className={`form-check-label small ${isProduction ? "text-muted" : ""}`}
                    htmlFor="tierApproveOnly"
                    title={approveOnlyTooltip}
                  >
                    Approve only <i className="fa fa-info-circle text-muted" title={approveOnlyTooltip} />
                  </label>
                </div>

                <div className="form-check mb-1">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="approvalTier"
                    id="tierDistribute"
                    value="approve_and_distribute"
                    checked={approvalTier === "approve_and_distribute"}
                    onChange={() => setApprovalTier("approve_and_distribute")}
                    disabled={!!actionLoading}
                  />
                  <label className="form-check-label small" htmlFor="tierDistribute" title={distributeTooltip}>
                    Approve &amp; share to feed <i className="fa fa-info-circle text-muted" title={distributeTooltip} />
                  </label>
                </div>

                <div className="form-check mb-1">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="approvalTier"
                    id="tierNotify"
                    value="approve_and_notify"
                    checked={approvalTier === "approve_and_notify"}
                    onChange={() => setApprovalTier("approve_and_notify")}
                    disabled={!!actionLoading}
                  />
                  <label className="form-check-label small" htmlFor="tierNotify" title={notifyTooltip}>
                    Approve, share to feed &amp; notify{" "}
                    <i className="fa fa-info-circle text-muted" title={notifyTooltip} />
                  </label>
                </div>
              </div>

              <h6 className="small fw-bold mb-1">Free to Pitch</h6>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="freeToPitch"
                  checked={freeToPitch}
                  onChange={(e) => setFreeToPitch(e.target.checked)}
                  disabled={!!actionLoading}
                />
                <label className="form-check-label small" htmlFor="freeToPitch" title={ftpTooltip}>
                  Allow free pitching <i className="fa fa-info-circle text-muted" title={ftpTooltip} />
                </label>
              </div>

              <h6 className="small fw-bold mb-1">Fee-based</h6>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="paidTogglePending"
                  checked={request.paid || false}
                  onChange={handleTogglePaid}
                  disabled={!!actionLoading}
                />
                <label className="form-check-label small" htmlFor="paidTogglePending">
                  Fee-based opportunity{" "}
                  <i
                    className="fa fa-info-circle text-muted"
                    title="Mark this as a paid/sponsored opportunity where sources may pay to be featured."
                  />
                </label>
              </div>

              <button
                type="button"
                className="btn btn-success w-100"
                onClick={handleApprove}
                disabled={!!actionLoading}
              >
                {actionLoading === "approve" ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-1" />
                    Approving...
                  </>
                ) : (
                  "Approve Request"
                )}
              </button>
            </div>

            {/* Re-evaluate box */}
            <div className="p-3 mb-3 border-start border-4 border-info">
              <h6 className="small fw-bold mb-2">Automation</h6>
              <p className="text-muted small mb-2">Run the approval pipeline again after fixing content issues.</p>
              <button
                type="button"
                className="btn btn-outline-info w-100"
                onClick={handleResubmitClick}
                disabled={!!actionLoading}
              >
                {actionLoading === "resubmit" ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-1" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fa fa-refresh me-1" />
                    Re-evaluate for Auto-Approval
                  </>
                )}
              </button>
            </div>

            {/* Disapprove box */}
            <div className="p-3 mb-3 border-start border-4 border-danger">
              <h6 className="small fw-bold mb-2">Disapprove</h6>
              <button
                type="button"
                className="btn btn-outline-danger w-100"
                onClick={handleGhost}
                disabled={!!actionLoading}
              >
                {actionLoading === "ghost" ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-1" />
                    Ghosting...
                  </>
                ) : (
                  "Ghost"
                )}
              </button>
            </div>

            <hr />

            {/* Future rules */}
            <div>
              <h6 className="text-muted small mb-2">Set rules for future requests</h6>
              <div className="d-flex flex-column gap-2">
                {request.media_outlet_id ? (
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <span className="small fw-medium">{request.media_outlet_name}</span>
                      {outletConfig && (
                        <span
                          className={`badge ms-2 ${outletConfig.auto_action === "ghost" ? "bg-danger" : "bg-secondary"}`}
                        >
                          {outletConfig.auto_action}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowOutletModal(true)}
                    >
                      Set Auto Action...
                    </button>
                  </div>
                ) : (
                  <div className="text-muted small fst-italic">No outlet assigned</div>
                )}
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="small fw-medium">{request.profile_name}</span>
                    {profileConfig && (
                      <span className="badge bg-secondary ms-2">
                        {profileConfig.auto_action === "defer" ? "defer to outlet" : profileConfig.auto_action}
                      </span>
                    )}
                    {!profileConfig && request.effective_profile_action && (
                      <span
                        className="badge ms-2"
                        style={{ border: "1px dashed #6c757d", color: "#6c757d", backgroundColor: "transparent" }}
                      >
                        {request.effective_profile_action === "manual_review" ? "default" : "disabled"}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowProfileModal(true)}
                  >
                    Set Auto Action...
                  </button>
                </div>
                <p className="text-muted small fst-italic mb-0">Rule changes apply to future requests only.</p>
              </div>
            </div>
          </>
        )}

        {/* Approved only — can upgrade to Distribute or Notify */}
        {isApproved && !isDistributed && !isNotified && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>
                <i className="fa fa-check-circle text-secondary me-2" />
                Approved at {qformat(request.approved_at, DATE_FORMATS.dateTime, "")}
              </span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleDistributeNow}
                  disabled={!!actionLoading}
                >
                  Distribute
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => setShowNotifyConfirm(true)}
                  disabled={!!actionLoading}
                >
                  Notify
                </button>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="ftpToggleApproved"
                  checked={request.free_to_pitch || false}
                  onChange={handleToggleFreeToPitch}
                  disabled={!!actionLoading}
                />
                <label className="form-check-label small" htmlFor="ftpToggleApproved">
                  Free to pitch
                </label>
              </div>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleGhostApproved}
                disabled={!!actionLoading}
              >
                Ghost
              </button>
            </div>
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="paidToggleApproved"
                checked={request.paid || false}
                onChange={handleTogglePaid}
                disabled={!!actionLoading}
              />
              <label className="form-check-label small" htmlFor="paidToggleApproved">
                Fee-based opportunity
              </label>
            </div>
          </div>
        )}

        {/* Distributed — can upgrade to Notify */}
        {isApproved && isDistributed && !isNotified && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>
                <i className="fa fa-check-circle text-primary me-2" />
                Approved + Feed at {qformat(request.distribution_approved_for_feed_at, DATE_FORMATS.dateTime, "")}
              </span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleBumpInFeed}
                  disabled={!!actionLoading || bumpInFeedDisabled}
                  title={bumpInFeedDisabled ? bumpInFeedDisabledTooltip : bumpInFeedTooltip}
                >
                  {actionLoading === "bump_to_top" ? "Bumping..." : "Bump in Feed"}
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => setShowNotifyConfirm(true)}
                  disabled={!!actionLoading}
                >
                  Notify Now
                </button>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="ftpToggleDistributed"
                  checked={request.free_to_pitch || false}
                  onChange={handleToggleFreeToPitch}
                  disabled={!!actionLoading}
                />
                <label className="form-check-label small" htmlFor="ftpToggleDistributed">
                  Free to pitch
                </label>
              </div>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleGhostApproved}
                disabled={!!actionLoading}
              >
                Ghost
              </button>
            </div>
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="paidToggleDistributed"
                checked={request.paid || false}
                onChange={handleTogglePaid}
                disabled={!!actionLoading}
              />
              <label className="form-check-label small" htmlFor="paidToggleDistributed">
                Fee-based opportunity
              </label>
            </div>
          </div>
        )}

        {/* Notified — terminal for distribution, but still allows ghost + ftp toggle */}
        {isApproved && isNotified && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>
                <i className="fa fa-check-circle text-success me-2" />
                Approved + Feed & Notified at{" "}
                {qformat(request.distribution_approved_for_notifications_at, DATE_FORMATS.dateTime, "")}
              </span>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleBumpInFeed}
                disabled={!!actionLoading || bumpInFeedDisabled}
                title={bumpInFeedDisabled ? bumpInFeedDisabledTooltip : bumpInFeedTooltip}
              >
                {actionLoading === "bump_to_top" ? "Bumping..." : "Bump in Feed"}
              </button>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="ftpToggleNotified"
                  checked={request.free_to_pitch || false}
                  onChange={handleToggleFreeToPitch}
                  disabled={!!actionLoading}
                />
                <label className="form-check-label small" htmlFor="ftpToggleNotified">
                  Free to pitch
                </label>
              </div>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleGhostApproved}
                disabled={!!actionLoading}
              >
                Ghost
              </button>
            </div>
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="paidToggleNotified"
                checked={request.paid || false}
                onChange={handleTogglePaid}
                disabled={!!actionLoading}
              />
              <label className="form-check-label small" htmlFor="paidToggleNotified">
                Fee-based opportunity
              </label>
            </div>
          </div>
        )}

        {/* Ghosted */}
        {isGhosted && (
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <i className="fa fa-ban text-danger me-2" />
              Ghosted at {qformat(request.ghosted_at, DATE_FORMATS.dateTime, "")}
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleUnghost}
              disabled={!!actionLoading}
            >
              {actionLoading === "unghost" ? "Unghosting..." : "Unghost"}
            </button>
          </div>
        )}
      </div>

      {/* Approve + Feed & Notify confirmation modal */}
      <Modal show={showNotifyConfirm} onHide={() => setShowNotifyConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Approve + Feed & Notify</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning mb-3">
            <i className="fa fa-exclamation-triangle me-2" />
            <strong>This action is irreversible.</strong> Notifications will be sent to matching experts immediately.
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="confirmFreeToPitch"
              checked={freeToPitch}
              onChange={(e) => setFreeToPitch(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="confirmFreeToPitch">
              Free to pitch
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setShowNotifyConfirm(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={isApproved ? handleNotifyNow : handleConfirmApproveNotify}
          >
            Yes, Approve + Feed & Notify
          </button>
        </Modal.Footer>
      </Modal>

      {/* Ghost approved request confirmation modal */}
      <Modal show={showGhostConfirm} onHide={() => setShowGhostConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Ghost Approved Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger mb-0">
            <i className="fa fa-exclamation-triangle me-2" />
            This request has already been approved. Are you sure you want to ghost it?
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setShowGhostConfirm(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={handleConfirmGhostApproved}>
            Yes, Ghost
          </button>
        </Modal.Footer>
      </Modal>

      {/* Outlet config modal */}
      {showOutletModal && request.media_outlet_id && (
        <OutletConfigModal
          outletId={request.media_outlet_id}
          outletName={request.media_outlet_name}
          currentConfig={outletConfig}
          outletInfo={{
            prestige: request.media_outlet_prestige,
            domainAuthority: request.media_outlet_domain_authority,
          }}
          onSave={(updated) => {
            onOutletConfigChange(updated);
            setShowOutletModal(false);
          }}
          onClose={() => setShowOutletModal(false)}
        />
      )}

      {/* Profile config modal */}
      {showProfileModal && (
        <MediaProfileConfigModal
          profileId={request.profile_id}
          profileName={request.profile_name}
          profileType="Id::Profile::MediaProfile"
          currentConfig={profileConfig}
          onSave={(updated) => {
            onProfileConfigChange(updated);
            setShowProfileModal(false);
          }}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
}

export default ApprovalPanel;
